/* ============================================================
   PILIH — pemilih v3.9 (ganti <select> asli)
   ============================================================
   Kenapa ia wujud (arahan user 2026-08-14): `<select>` asli ialah
   kawalan sistem operasi. Kotak tertutupnya boleh dicat dengan CSS,
   tetapi SENARAI yang terbuka bila diklik dilukis oleh Windows —
   putih, font sistem, bucu tajam — dan CSS langsung tidak boleh
   menyentuhnya. Setiap pemilih lain dalam v3.9 (Sort, Group
   Switcher) ialah menu yang kita bina sendiri, jadi `<select>`
   berdiri di tengah-tengah mereka sebagai satu-satunya kawalan yang
   berkelakuan lain.

   Bentuk butang MENGIKUT `.s-select` yang digantikannya — tinggi,
   bucu dan garis yang sama — supaya ia kekal terbaca sebagai medan
   borang, bukan butang tindakan. Menunya memakai `.s-pop` yang sedia
   ada, sama seperti SusunMenuV39.

   ⭐ MENU DITERBANGKAN KE <body> (portal) — arahan user 2026-08-18.
   Sebelum ini menu ialah anak mutlak (`position:absolute`) pemboleh
   ubah butang. Bila pemilih ini duduk dalam Control Panel (lajur
   kanan Workspace), kad panel membawa `overflow:hidden` untuk bucu
   bulatnya DAN lajur itu `overflow-y:auto` — jadi bila senarai agent
   panjang, hujung menu yang terjulur keluar kad DIKERAT dan langsung
   tidak dapat ditatal (dilaporkan user: "tak dapat nak scroll").
   Menariknya ke <body> dengan `position:fixed` yang dikunci pada
   kotak butang membebaskannya daripada SEMUA bekas overflow — kad,
   lajur, badan modal — sekali gus. `max-height`/`overflow-y:auto`
   pada `.s-pop` barulah benar-benar berfungsi kerana tiada lagi yang
   mengeratnya dari luar.

   ⚠️ Gaya kedudukan ditulis INLINE pada instance ini sahaja — kelas
   `.s-pop` (dikongsi SusunMenuV39/GroupSwitcher yang TIDAK diportal)
   tidak disentuh, jadi menu lain kekal `position:absolute` seperti
   asal.

   Bentuk setiap pilihan:  { id, label, nota?, disabled? }
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

export default function PilihV39({
    nilai,
    pilihan = [],
    onPilih,
    disabled = false,
    ariaLabel,
    autoFocus = false,
    /* `auto` untuk pemilih yang duduk dalam satu baris bersama kawalan
       lain (cth "Setiap 30 minit"); lalai penuh lebar untuk medan borang. */
    lebar = '100%',
    tinggi = 36,
    saizTeks = 13.5,
    lebarMenu,
    /* Menu dijajar ke kanan bila butang duduk di hujung kanan bar —
       sama sebab seperti SusunMenuV39: menu yang bermula dari tepi kiri
       melimpah keluar bekas. */
    jajarKanan = false,
    kosong = 'Pilih',
}) {
    const [buka, setBuka] = useState(false);
    const rujuk = useRef(null);       // pembalut butang
    const menuRujuk = useRef(null);   // menu terapung (dalam portal)
    const [pos, setPos] = useState(null);

    /* Kira kedudukan menu terapung daripada kotak butang. Menu duduk
       dalam portal ke <body>, jadi ia diletak dengan `position:fixed`
       (koordinat viewport) — bukan relatif kepada butang lagi. */
    const kiraPos = useCallback(() => {
        const btn = rujuk.current;
        if (!btn) return;
        const r = btn.getBoundingClientRect();
        const jurang = 6;
        const tepi = 8; // ruang minimum dari tepi skrin

        const ruangBawah = window.innerHeight - r.bottom - tepi;
        const ruangAtas = r.top - tepi;
        /* Buka ke atas HANYA bila ruang bawah sempit DAN ruang atas
           lebih lega — supaya menu tidak terjulur keluar bawah skrin. */
        const keAtas = ruangBawah < 200 && ruangAtas > ruangBawah;
        const ruang = (keAtas ? ruangAtas : ruangBawah) - jurang;
        const hadTinggi = Math.max(120, Math.min(320, ruang));

        /* Lebar menu = `lebarMenu` (px) bila diberi, jika tidak selebar
           butang (kelakuan `minWidth:'100%'` lama). */
        const lebarKotak = typeof lebarMenu === 'number' ? lebarMenu : r.width;

        /* Jepit mendatar supaya menu tidak terjulur keluar tepi skrin. */
        let kiri = 'auto';
        let kanan = 'auto';
        if (jajarKanan) {
            kanan = Math.round(Math.max(tepi, window.innerWidth - r.right));
        } else {
            kiri = Math.round(Math.min(
                Math.max(tepi, r.left),
                window.innerWidth - lebarKotak - tepi,
            ));
            if (kiri < tepi) kiri = tepi;
        }

        setPos({
            top: keAtas ? 'auto' : Math.round(r.bottom + jurang),
            bottom: keAtas ? Math.round(window.innerHeight - r.top + jurang) : 'auto',
            left: kiri,
            right: kanan,
            minWidth: lebarKotak,
            maxHeight: hadTinggi,
        });
    }, [jajarKanan, lebarMenu]);

    /* Buka/tutup. Kedudukan dikira DALAM pengendali klik ini (bukan
       dalam effect) supaya kotak butang sudah sedia diukur dan menu
       muncul di tempat betul pada lukisan pertama — tiada kelipan, dan
       tiada `setState` segerak dalam effect (peraturan lint React). */
    const togolMenu = () => {
        if (buka) { setBuka(false); return; }
        kiraPos();
        setBuka(true);
    };

    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => {
            if (rujuk.current && rujuk.current.contains(e.target)) return;
            /* Menu kini di luar pembalut butang (portal) — tanpa semakan
               ini, klik pada pilihan dikira "di luar" dan menu tertutup
               sebelum onClick pilihan sempat berjalan. */
            if (menuRujuk.current && menuRujuk.current.contains(e.target)) return;
            setBuka(false);
        };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        /* Menu dikunci pada kotak butang. Bila mana-mana bekas menatal
           (lajur kanan Control Panel, badan modal) atau tetingkap berubah
           saiz, kira semula supaya menu terus melekat.
           `capture:true` WAJIB — peristiwa `scroll` tidak menggelembung,
           jadi tanpa fasa tangkap kita terlepas tatalan bekas dalaman. */
        const semula = () => kiraPos();
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        window.addEventListener('resize', semula);
        window.addEventListener('scroll', semula, true);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
            window.removeEventListener('resize', semula);
            window.removeEventListener('scroll', semula, true);
        };
    }, [buka, kiraPos]);

    /* Menu tidak boleh kekal di skrin bila kawalan dilumpuhkan di tengah
       jalan (cth larian bermula sambil menu terbuka). Dikira, bukan
       disimpan — state yang perlu diselaraskan dengan prop akan terpesong
       cepat atau lambat. */
    const nampakMenu = buka && !disabled;

    const kini = pilihan.find((p) => String(p.id) === String(nilai)) || null;

    return (
        <div ref={rujuk} style={{ position: 'relative', width: lebar }}>
            <button
                type="button"
                className="s-pick"
                style={{ height: tinggi, fontSize: saizTeks }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={nampakMenu}
                aria-label={ariaLabel}
                autoFocus={autoFocus}
                onClick={togolMenu}
            >
                <span className="s-pick__t" style={{ color: kini ? 'var(--s-ink)' : 'var(--s-ink-3)' }}>
                    {kini ? kini.label : kosong}
                </span>
                <ChevronDown size={14} strokeWidth={2} style={{ flex: 'none', color: 'var(--s-ink-3)' }} />
            </button>

            {nampakMenu && pos && createPortal(
                <div
                    ref={menuRujuk}
                    className="s-pop"
                    role="listbox"
                    aria-label={ariaLabel}
                    style={{
                        /* Batalkan kedudukan mutlak `.s-pop` — instance ini
                           terapung pada viewport, bukan relatif butang. */
                        position: 'fixed',
                        top: pos.top,
                        bottom: pos.bottom,
                        left: pos.left,
                        right: pos.right,
                        minWidth: pos.minWidth,
                        maxWidth: 'calc(100vw - 16px)',
                        maxHeight: pos.maxHeight,
                        zIndex: 10040,
                    }}
                >
                    {pilihan.length === 0 && (
                        <p style={{ margin: 0, padding: '8px 9px', fontSize: 12.5, color: 'var(--s-ink-3)' }}>
                            Tiada pilihan.
                        </p>
                    )}
                    {pilihan.map((p) => {
                        const aktif = String(p.id) === String(nilai);
                        return (
                            <button
                                key={p.id}
                                type="button"
                                role="option"
                                aria-selected={aktif}
                                className="s-pop__row"
                                disabled={!!p.disabled}
                                style={p.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : null}
                                onClick={() => { if (p.disabled) return; setBuka(false); onPilih?.(p.id); }}
                            >
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{
                                        display: 'block', fontSize: 13,
                                        color: aktif ? 'var(--s-ink)' : 'var(--s-ink-2)',
                                    }}>
                                        {p.label}
                                    </span>
                                    {p.nota && (
                                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--s-ink-3)', marginTop: 2 }}>
                                            {p.nota}
                                        </span>
                                    )}
                                </span>
                                {aktif && <Check size={13} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
                            </button>
                        );
                    })}
                </div>,
                document.body,
            )}
        </div>
    );
}
